import { GameConfig } from "@/config/GameConfig";
import type { BiteKind } from "@/core/types";
import type { BiteBonusResult, BiteBonusSource, ManeuverType, SkillMoment } from "./types";

export interface RecordBiteBonusStepInput {
  /** This step's credited skill moment, or `null` if nothing was credited. `SkillMoment` only ever exists when it awarded positive points (see `ManeuverTracker.recordMove`) - so "credited" here always means "positively credited", never a duplicate-anchor or exhausted-occurrence detection (both of which never produce a `SkillMoment` at all). */
  skillMoment: SkillMoment | null;
  /** The kind of fruit eaten on this exact executed movement step, or `null` if none was. Only ever SAFE/RUSH/CHAOS/STARFRUIT - entering the gate or a completed SPEED TRIAL are never reported here. */
  fruitKind: BiteKind | null;
  /**
   * PR13-B: true only on the exact executed movement step a WORMHOLE
   * boundary wrap was authoritatively committed - see
   * `LevelGame.tick()`'s wrap-commit signal (captured only after every
   * lethal destination check at the wrapped cell has already passed and
   * the head has actually been committed there). Independent of
   * `skillMoment` - both can be true on the same step (a geometric
   * maneuver and a successful wrap can coincide). Optional/defaults to
   * `false` so every pre-PR13-B call site (and every existing test)
   * keeps working unchanged.
   */
  wormholeWrapSuccess?: boolean;
}

interface Opener {
  source: BiteBonusSource;
  windowLen: number;
  maneuvers: readonly ManeuverType[] | null;
}

/**
 * TRICK BITE / CLUTCH BITE: connects a positively credited "opener" - one
 * of the four geometric maneuvers, or (PR13-B) a successful WORMHOLE
 * boundary wrap - to the fruit-choice loop, per-run state. An opener
 * opens (or refreshes) a window covering the next N executed movement
 * steps (3 for a geometric maneuver, 2 for WORMHOLE - see
 * `GameConfig.scoring.biteBonus`); a fruit eaten inside that window earns
 * TRICK BITE. A fruit eaten on the *exact same* step the opener fires
 * earns the stronger CLUTCH BITE instead - the two never stack for the
 * same fruit. Both are flat integers, deliberately never touched by
 * FLOW/speed/diminishing-returns (those are `ManeuverTracker`'s concern,
 * already fully resolved by the time `skillMoment` reaches here) -
 * WORMHOLE never receives any of that either, since it isn't a geometric
 * maneuver at all and never goes through `ManeuverTracker`.
 *
 * There is exactly ONE shared window, whichever source opened it - never
 * two independently-paying systems. When two openers are both "new" on
 * the same step (or a fresh opener arrives while a window from an earlier
 * step is already open), the non-degrading overlap rule applies: the
 * effective remaining window is the greater of the current remaining
 * steps and the new opener's own full requested length, and source
 * attribution follows whichever length wins (the newest opener becomes
 * authoritative on a tie or an improvement, never on a strictly shorter
 * request) - a new opener can never shorten a better active opportunity,
 * and repeated openers never add up past their own single window length.
 *
 * `LevelGame` is the only caller, once per safe executed movement step
 * (never for a lethal one, never for the automatic gate-exit walk, never
 * for a rejected input or the passage of real time) - the exact same
 * cadence `ManeuverTracker.recordMove()` already follows.
 */
export class TrickBiteTracker {
  private windowRemaining = 0;
  private windowOpenedBy: readonly ManeuverType[] | null = null;
  private windowSource: BiteBonusSource | null = null;
  private trickBiteTotal = 0;
  private clutchBiteTotal = 0;
  private bonusPointsTotal = 0;
  private lastResult: BiteBonusResult | null = null;
  /**
   * PR26 CHANGE 7: how many WORMHOLE-sourced TRICK/CLUTCH awards this
   * WORMHOLE activation has produced so far - shared across both kinds
   * (see `resolveAwardPoints()`). Scoped to ONE activation: `LevelGame`
   * calls `resetWormholeActivationCounter()` only when a WORMHOLE CHAOS
   * roll starts a genuinely new activation, never on a refresh of an
   * already-active WORMHOLE. Maneuver-sourced awards never touch this
   * counter at all.
   */
  private wormholeAwardCounter = 0;

  /** Reports one safe executed movement step. Returns the bonus awarded (if any), or `null`. */
  recordStep(input: RecordBiteBonusStepInput): BiteBonusResult | null {
    const maneuverOpener: Opener | null = input.skillMoment
      ? { source: "maneuver", windowLen: GameConfig.scoring.biteBonus.windowMoves, maneuvers: input.skillMoment.maneuvers.map((m) => m.type) }
      : null;
    const wormholeOpener: Opener | null = input.wormholeWrapSuccess
      ? { source: "wormhole", windowLen: GameConfig.scoring.biteBonus.wormholeWindowMoves, maneuvers: null }
      : null;
    const fruitEaten = input.fruitKind !== null;

    if (maneuverOpener || wormholeOpener) {
      // At least one opener fired this exact step. Same-tick priority: a
      // successful wrap always wins CLUTCH attribution over a co-occurring
      // geometric skill moment, even though the *window* merge below (by
      // requested length) may still end up authoritative to the maneuver -
      // those are deliberately two separate decisions.
      if (fruitEaten) {
        const source: BiteBonusSource = wormholeOpener ? "wormhole" : "maneuver";
        const points = this.resolveAwardPoints(GameConfig.scoring.biteBonus.clutchBite, source);
        this.clutchBiteTotal += 1;
        this.bonusPointsTotal += points;
        const result: BiteBonusResult = { kind: "clutch", points, source };
        this.lastResult = result;
        // Resolved before the (unconditional, below) window merge so a
        // *future* fruit still gets a fresh window from this same step's
        // opener(s) - same pattern as the pre-PR13-B geometric-only logic.
        this.mergeOpener(maneuverOpener, wormholeOpener);
        return result;
      }

      this.mergeOpener(maneuverOpener, wormholeOpener);
      return null;
    }

    if (fruitEaten && this.windowRemaining > 0) {
      // TRICK BITE: fruit eaten while an earlier opener's window is still
      // open. Consumes the window - only one TRICK BITE per fruit, and per
      // window. Attribution follows whichever source is currently
      // authoritative for the open window.
      const source: BiteBonusSource = this.windowSource ?? "maneuver";
      const points = this.resolveAwardPoints(GameConfig.scoring.biteBonus.trickBite, source);
      this.trickBiteTotal += 1;
      this.bonusPointsTotal += points;
      const result: BiteBonusResult = { kind: "trick", points, source };
      this.lastResult = result;
      this.windowRemaining = 0;
      this.windowOpenedBy = null;
      this.windowSource = null;
      return result;
    }

    // Neither opened nor consumed this step - Pausing, resume countdowns,
    // rejected inputs, and real-time passage never reach this method at
    // all (LevelGame only calls it once per safe executed movement step),
    // so this decrement alone is "eating without a window" or "an idle
    // step while the window counts down".
    if (this.windowRemaining > 0) {
      this.windowRemaining -= 1;
      if (this.windowRemaining === 0) {
        this.windowOpenedBy = null;
        this.windowSource = null;
      }
    }
    return null;
  }

  /**
   * Non-degrading overlap rule: picks the single "winning" opener between
   * up to two same-step candidates (by requested window length - a
   * geometric maneuver's 3 always outweighs WORMHOLE's 2, so a same-step
   * co-occurrence is never ambiguous), then merges it against the
   * currently active window. The effective remaining window becomes the
   * greater of the current remaining steps and the winning opener's own
   * full length; source attribution follows the new opener whenever it
   * matches or improves the remaining length, and is preserved unchanged
   * whenever the new opener would have shortened it. Never adds the two
   * together, never awards two future bonuses.
   */
  private mergeOpener(maneuverOpener: Opener | null, wormholeOpener: Opener | null): void {
    const opener =
      !maneuverOpener ? wormholeOpener : !wormholeOpener ? maneuverOpener : maneuverOpener.windowLen >= wormholeOpener.windowLen ? maneuverOpener : wormholeOpener;
    if (!opener) return;

    if (opener.windowLen >= this.windowRemaining) {
      this.windowRemaining = opener.windowLen;
      this.windowSource = opener.source;
      this.windowOpenedBy = opener.maneuvers;
    }
    // opener.windowLen < this.windowRemaining: strictly shorter than the
    // already-active window - preserve it untouched, source included.
  }

  /** How many more executed movement steps the TRICK/CLUTCH BITE window stays open - 0 means inactive. What the "TRICK BITE READY: N"/"WORMHOLE BITE READY: N" indicator shows. */
  get windowRemainingMoves(): number {
    return this.windowRemaining;
  }

  get isWindowActive(): boolean {
    return this.windowRemaining > 0;
  }

  /** The maneuver type(s) that opened/most-recently-refreshed the currently active window - `null` whenever the window is inactive OR its currently-authoritative source is WORMHOLE (which has no maneuvers of its own). */
  get windowOpenedByManeuvers(): readonly ManeuverType[] | null {
    return this.windowRemaining > 0 ? this.windowOpenedBy : null;
  }

  /** PR13-B: which opener source is currently authoritative for the active window - `null` whenever the window is inactive. Drives the source-specific "WORMHOLE BITE READY" vs. generic "TRICK BITE READY" indicator. */
  get windowOpenedBySource(): BiteBonusSource | null {
    return this.windowRemaining > 0 ? this.windowSource : null;
  }

  get trickBiteCount(): number {
    return this.trickBiteTotal;
  }

  get clutchBiteCount(): number {
    return this.clutchBiteTotal;
  }

  /** Sum of every awarded TRICK/CLUTCH BITE's flat points - added directly into `LevelGame`'s Skill Score total, alongside `ManeuverTracker.skillScore`. */
  get bonusPoints(): number {
    return this.bonusPointsTotal;
  }

  /** The most recent TRICK/CLUTCH BITE result this run, or `null` if none yet - for the `?qa=scoring` overlay only. */
  get lastBonusResult(): BiteBonusResult | null {
    return this.lastResult;
  }

  /**
   * PR13-B: zeroes the active window (remaining steps + source +
   * maneuvers) without touching cumulative totals (`trickBiteCount`/
   * `clutchBiteCount`/`bonusPoints`/`lastBonusResult`) - called by
   * `LevelGame` the instant gate entry/EXITING begins, so the window can
   * never be exploited to earn a bonus during or after the automatic exit
   * walk. Any bonus already awarded (e.g. by the very fruit that opened
   * the gate, on an earlier tick) is untouched - only the *forward-looking*
   * opportunity clears.
   */
  clearWindow(): void {
    this.windowRemaining = 0;
    this.windowOpenedBy = null;
    this.windowSource = null;
  }

  /**
   * PR26 CHANGE 7: applies the WORMHOLE-sourced diminishing scale to one
   * TRICK/CLUTCH award - 1st (this activation) = x1, 2nd = x0.5, 3rd =
   * x0.25, 4th+ = x0 (i.e. 0 points, though `trickBiteCount`/
   * `clutchBiteCount` still increment - the same "still counted, worth
   * nothing further" convention `ManeuverTracker`'s own occurrence cap
   * uses). A maneuver-sourced award is completely untouched: returns
   * `baseValue` unchanged and never advances the counter, since the
   * counter and cap exist ONLY to bound one WORMHOLE activation from
   * becoming an unbounded Score engine (see CHANGE 7's own design doc) -
   * they were never meant to affect the maneuver-opened path, which
   * already has its own separate, unrelated bound (Audit C: capped at
   * 12 openings/run via `ManeuverTracker`'s own occurrence caps).
   */
  private resolveAwardPoints(baseValue: number, source: BiteBonusSource): number {
    if (source !== "wormhole") return baseValue;
    this.wormholeAwardCounter += 1;
    const multiplier = this.wormholeAwardCounter === 1 ? 1 : this.wormholeAwardCounter === 2 ? 0.5 : this.wormholeAwardCounter === 3 ? 0.25 : 0;
    return Math.round(baseValue * multiplier);
  }

  /**
   * PR26 CHANGE 7: resets the WORMHOLE-sourced award-scaling counter to 0
   * - called by `LevelGame` exactly once, only when a WORMHOLE CHAOS roll
   * starts a genuinely NEW activation (WORMHOLE was not already active
   * the moment this fires). A REFRESH of an already-active WORMHOLE must
   * never call this - "that is the same activation continuing." Never
   * touches `trickBiteCount`/`clutchBiteCount`/`bonusPoints`/
   * `lastBonusResult` - only the forward-looking scaling counter resets.
   */
  resetWormholeActivationCounter(): void {
    this.wormholeAwardCounter = 0;
  }
}
